import { useAxisNames } from 'features/assetModeling'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import React, { useMemo } from 'react'
import {
  GeckoComponentModelType,
  GeckoComponentShape,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoShipAssetModel,
} from 'types'

import { GeckoMeasurementLineItem } from './GeckoMeasurementLineItem'

/**
 * A component for showing component geometry (either a point or a rect) as a
 * "line item"- putting x- and y-coordinate names off to the left and their
 * values off to the right.
 * @param geometry        The geometry to depict (either a point or a rect)
 * @param showX           Whether to show the x-coordinate (defaults to true)
 * @param showY           Whether to show the y-coordinate (defaults to true)
 * @param variant         The string variant to use (affects string length)
 * @param showLoading     Whether to show a "Loading..." filler string
 * @constructor
 */
export const GeckoGeometryLineItem: React.FC<{
  geometry: GeckoComponentShape | undefined
  componentModelType: GeckoComponentModelType | undefined
  showX?: boolean
  showY?: boolean
  showLoading?: boolean
}> = ({
  geometry,
  showX = true,
  showY = true,
  showLoading,
  componentModelType,
}) => {
  const { assetModel } = useAssetVizInternal()
  const { xAxisName, yAxisName } = useAxisNames(geometry, componentModelType)

  // Don't show coordinates for BasicShipHull
  showX =
    componentModelType === GeckoComponentModelType.BasicShipHull ? false : showX
  showY =
    componentModelType === GeckoComponentModelType.BasicShipHull ? false : showY

  const geometryToUse = useMemo(() => {
    if (!geometry) return { x: undefined, y: undefined }
    const x = geometry.x
    let y = geometry.y
    if (componentModelType === GeckoComponentModelType.ShipDeck) {
      if (
        assetModel instanceof GeckoShipAssetModel &&
        !!assetModel.data.frame_size
      ) {
        y = geometry.y.convertToFrames(assetModel.data.frame_size)
      }

      if (x instanceof GeckoMeasurement && y instanceof GeckoMeasurement) {
        return {
          x: x.abs(),
          y: y,
        }
      } else if (
        x instanceof GeckoMeasurementRange &&
        y instanceof GeckoMeasurementRange
      ) {
        return {
          x: GeckoMeasurementRange.CreateFromValues(
            x.min.abs().num,
            x.max.abs().num,
            x.unit
          ),
          y: GeckoMeasurementRange.CreateFromValues(
            y.min.abs().num,
            y.max.abs().num,
            y.unit
          ),
        }
      }
    }
    return geometry
  }, [componentModelType, geometry, assetModel])

  return !geometry ? undefined : (
    <>
      {showX && (
        <GeckoMeasurementLineItem
          name={xAxisName}
          measurement={geometryToUse.x}
          showLoading={showLoading}
          variant='geometric'
        />
      )}
      {showY && (
        <GeckoMeasurementLineItem
          name={yAxisName}
          measurement={geometryToUse.y}
          showLoading={showLoading}
          variant='geometric'
        />
      )}
    </>
  )
}
